/**
 * Sdílené prvky rozhraní.
 *
 * Jeden soubor schválně, stejně jako v pm-app: komponenty se pak dají mezi
 * aplikacemi kopírovat beze změny cest. Stávající komponenty (Card, KpiCard,
 * GradientHeader, Pill, ConfirmDialog) zůstávají ve svých souborech a drží si
 * props, tady je jenom to, co v adminu dosud chybělo.
 *
 * Zadání: docs/redesign-2-zadani.md verze 1.1, kapitola 4.4.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronLeft, ChevronRight, Check, X, Pencil, Trash2, AlertTriangle, CheckCircle2, Clock, RefreshCw, MoreHorizontal } from "lucide-react";
import { useUzkaObrazovka, useZamekRolovani } from "../lib/obrazovka.js";
import { ton as tonPodleKlice } from "../lib/tony.js";
import { HlavickaDlazdice } from "./PrehledDlazdice.jsx";

/* --- Kontejner stránky --------------------------------------------------- */

/**
 * Obal obsahu stránky. Do 2. 9. 2026 si každá stránka řešila okraje sama a
 * bylo jich přes třicet různých kombinací; `/moje-vyplata` neměla obal vůbec
 * a obsah seděl na hraně obrazovky.
 *
 * `sirka`:
 *   "bezna"  výchozí, max-w-7xl
 *   "plna"   bez omezení šířky (kanban, kalendář, detail klientky)
 *   "uzka"   max-w-4xl (nastavení, formulářové stránky)
 */
export function Stranka({ sirka = "bezna", className = "", children, ...rest }) {
  const sirky = { bezna: "max-w-7xl", plna: "max-w-none", uzka: "max-w-4xl" };
  return (
    <div
      className={`mx-auto w-full ${sirky[sirka] || sirky.bezna} space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/* --- Okno ---------------------------------------------------------------- */

/**
 * Okno. Na telefonu vyjíždí zespodu jako sheet a končí u spodní hrany
 * obrazovky, na počítači je vycentrované. Patka se odlepí od home indikátoru
 * a celé okno se opře o klávesnici, takže pole, do kterého se píše, zůstane
 * vidět (viz src/lib/klavesnice.js).
 *
 * Používá se tak, že se do něj obalí obsah stávajícího okna. Stavy, handlery
 * a formuláře uvnitř zůstávají, jen se zahodí vlastní překryv a rám.
 */
export function Modal({ open, onClose, title, header, plnaHlavicka = false, footer, size = "md", plne = false, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const naKlavesu = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", naKlavesu);

    // Samotné overflow: hidden iOS Safari ignoruje, prstem jde pod otevřeným
    // oknem pořád posouvat stránkou. Drží až position: fixed; posun stránky se
    // předtím zapamatuje a po zavření vrátí, jinak okno skočí na začátek.
    const y = window.scrollY;
    const b = document.body;
    const puvodni = { overflow: b.style.overflow, position: b.style.position, top: b.style.top, width: b.style.width };
    b.style.overflow = "hidden";
    b.style.position = "fixed";
    b.style.top = `-${y}px`;
    b.style.width = "100%";

    return () => {
      document.removeEventListener("keydown", naKlavesu);
      Object.assign(b.style, puvodni);
      window.scrollTo(0, y);
    };
  }, [open, onClose]);

  if (!open) return null;
  const sirky = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl", full: "max-w-[92rem]" };
  const vyska = size === "full"
    ? "h-[calc(var(--vv-vyska,100vh)*0.92)]"
    : plne ? "h-[calc(var(--vv-vyska,100vh)*0.92)] sm:h-auto" : "";

  return createPortal(
    <div
      className="nad-klavesnici fixed inset-0 z-50 flex animate-fade-in items-end justify-center overflow-hidden overscroll-none bg-brand-dark/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => e.target === ref.current && onClose?.()}
      ref={ref}
      role="dialog"
      aria-modal="true"
    >
      <div className={`flex max-h-[calc(var(--vv-vyska,100vh)*0.92)] w-full ${vyska} ${sirky[size] || sirky.md} animate-sheet-up flex-col rounded-t-2xl bg-white shadow-2xl sm:animate-zoom-in sm:rounded-2xl`}>
        {header ? (
          // plnaHlavicka: hlavička si kreslí vlastní pozadí přes celou šířku
          // okna (tmavý pruh HR oken). Bez ní se obalí jako běžná lišta.
          plnaHlavicka ? header : <header className="border-b border-slate-200 px-4 py-3 sm:px-5">{header}</header>
        ) : (
          <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
            <h2 className="truncate text-base font-bold text-slate-900">{title}</h2>
            <button type="button" onClick={onClose} className="btn-ikona" aria-label="Zavřít" title="Zavřít">
              <X className="h-5 w-5" />
            </button>
          </header>
        )}
        {/* Roluje se jen nahoru a dolů: touch-action zahodí vodorovné tahy ještě
            než z nich prohlížeč udělá posun, overscroll-contain zabrání
            propadnutí rolování na stránku pod oknem. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4 sm:px-5" style={{ touchAction: "pan-y" }}>
          {children}
        </div>
        {footer && (
          <footer className="patka-okna patka-dole border-t border-slate-200 px-4 pt-3.5 sm:px-5 sm:pt-3" data-patka-okna>
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Tmavá hlavička okna: ikona, název, křížek. Stejný pruh jako hlavička stránky
 * a dlaždic na Přehledu, jen menší. Posílá se do `Modal` jako `header`
 * spolu s `plnaHlavicka`.
 */
export function HlavickaOkna({ ikona: Ikona, nazev, popis, odznaky, onClose }) {
  return (
    <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-5">
      <div className="flex items-center gap-3">
        {Ikona && (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 sm:h-10 sm:w-10">
            <Ikona className="h-5 w-5" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-serif text-lg font-bold leading-tight">{nazev}</span>
          {popis && <span className="block truncate text-xs text-white/70">{popis}</span>}
          {odznaky && <span className="mt-1 flex flex-wrap gap-1.5">{odznaky}</span>}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Zavřít"
          title="Zavřít"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/** Odznak do tmavé hlavičky okna. */
export function OdznakOkna({ ton = "bily", children }) {
  const tony = {
    bily: "bg-white/15 text-white",
    zeleny: "bg-emerald-500/25 text-white",
    zluty: "bg-amber-500/25 text-white",
    sedy: "bg-slate-500/40 text-white",
    ruzovy: "bg-rose-500/30 text-white",
    modry: "bg-sky-500/30 text-white",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${tony[ton] || tony.bily}`}>
      {children}
    </span>
  );
}

/* --- Pole ---------------------------------------------------------------- */

/** Popisek nad polem a nápověda pod ním, aby se při přeskládání neodtrhly. */
export function Field({ label, hint, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="label">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

/* --- Rozbalovací výběr --------------------------------------------------- */

/**
 * Výběr jedné z možností: štítek v pilulce vlevo, hodnota tučně, šipka vpravo.
 * Vzor je výběr portfolia v pm-app.
 *
 * Používá se všude, kde by se řada pilulek nevešla na šířku. Řada, která
 * roluje do strany, se v adminu nepoužívá: první a poslední položka byla
 * uříznutá v půlce slova a nic neříkalo, že se s pruhem dá hýbat.
 */
export function RozbalovaciVyber({ stitek, items, value, onChange, className = "", "aria-label": ariaLabel }) {
  const [otevreno, setOtevreno] = useState(false);
  const obal = useRef(null);
  const aktualni = items.find((x) => String(x.key) === String(value));

  useEffect(() => {
    if (!otevreno) return undefined;
    const mimo = (e) => { if (!obal.current?.contains(e.target)) setOtevreno(false); };
    const esc = (e) => e.key === "Escape" && setOtevreno(false);
    document.addEventListener("mousedown", mimo);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", mimo); document.removeEventListener("keydown", esc); };
  }, [otevreno]);

  return (
    <div ref={obal} className={`relative min-w-0 ${className}`}>
      <span className="flex min-h-[56px] min-w-0 items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3.5">
        {stitek && (
          <span className="flex h-[38px] shrink-0 items-center rounded-full bg-brand-light px-3.5 text-sm font-bold text-brand-deep">
            {stitek}
          </span>
        )}
        <button
          type="button"
          onClick={() => setOtevreno((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={otevreno}
          aria-label={ariaLabel}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 self-stretch text-left text-[18px] font-bold text-slate-900"
        >
          <span className="flex min-w-0 items-center gap-2.5">
            {aktualni?.ikona && <aktualni.ikona className="h-5 w-5 shrink-0 text-brand" />}
            <span className="min-w-0 truncate">{aktualni?.label ?? "—"}</span>
          </span>
          <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${otevreno ? "rotate-180" : ""}`} />
        </button>
      </span>

      {otevreno && (
        <ul
          role="listbox"
          className="absolute inset-x-0 top-[62px] z-[70] max-h-[60vh] overflow-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl"
        >
          {items.map((x) => {
            const vybrano = String(x.key) === String(value);
            return (
              <li key={x.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={vybrano}
                  onClick={() => { onChange?.(x.key); setOtevreno(false); }}
                  className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[17px] font-semibold transition ${
                    vybrano ? "bg-brand-light text-brand-deep" : "text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {x.ikona && <x.ikona className={`h-5 w-5 shrink-0 ${vybrano ? "text-brand" : "text-slate-500"}`} />}
                  <span className="min-w-0 flex-1 truncate">{x.label}</span>
                  <Check className={`h-5 w-5 shrink-0 ${vybrano ? "text-brand" : "opacity-0"}`} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* --- Záložky ------------------------------------------------------------- */

/**
 * Řada přepínačů v oválu. Nejvýš tři jako pilulky, od čtyř se vždycky použije
 * rozbalovací výběr (rozhodnutí z fáze 0). A i tři se přepnou na výběr, když
 * se na šířku nevejdou; rozhoduje měření, ne počet znaků.
 */
export function Zalozky({ items, value, onChange, className = "", stitek = "Sekce", "aria-label": ariaLabel }) {
  const obal = useRef(null);
  const rada = useRef(null);
  const [nevejde, setNevejde] = useState(false);

  const klice = items.map((x) => `${x.key}:${typeof x.label === "string" ? x.label : ""}`).join("|");
  useLayoutEffect(() => {
    const el = obal.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const zmer = () => {
      const potreba = rada.current?.scrollWidth || 0;
      const kDispozici = el.clientWidth;
      if (!potreba || !kDispozici) return;
      setNevejde(potreba > kDispozici + 1);
    };
    zmer();
    const ro = new ResizeObserver(zmer);
    ro.observe(el);
    document.fonts?.ready?.then(zmer).catch(() => {});
    return () => ro.disconnect();
  }, [klice]);

  if (items.length > 3 || nevejde) {
    return (
      <div ref={obal} className={`min-w-0 ${className}`}>
        <RozbalovaciVyber stitek={stitek} items={items} value={value} onChange={onChange} aria-label={ariaLabel} />
      </div>
    );
  }

  return (
    <div ref={obal} role="group" aria-label={ariaLabel} className={`min-w-0 w-full sm:w-auto ${className}`}>
      <div ref={rada} className="flex w-full items-center overflow-hidden rounded-full border border-brand/30 bg-white sm:inline-flex sm:w-max">
        {items.map((x) => (
          <button
            key={x.key}
            type="button"
            onClick={() => onChange?.(x.key)}
            aria-pressed={String(value) === String(x.key)}
            className={`min-h-[48px] shrink-0 grow basis-0 whitespace-nowrap px-4 text-base font-semibold transition sm:min-h-0 sm:grow-0 sm:basis-auto sm:px-3 sm:py-1.5 sm:text-sm ${
              String(value) === String(x.key) ? "bg-brand text-white" : "bg-white text-brand-deep hover:bg-brand-light"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* --- Listování obdobím --------------------------------------------------- */

/**
 * Šipka, název období, šipka. Prostřední prvek se roztáhne, na kraje zbydou
 * šipky s dotykovou velikostí. Tlačítko "na dnešek" se kreslí, jen když je
 * kam se vrátit, jinak by zabíralo řádek zbytečně.
 */
export function ListovaniMesice({ popis, onZpet, onVpred, zpetVypnuto, vpredVypnuto, naDnesek, popisDneska = "Na tento měsíc", className = "" }) {
  const uzka = useUzkaObrazovka();
  return (
    <div className={`flex ${uzka ? "flex-col" : "flex-row items-center"} gap-2 ${className}`}>
      <div className="flex flex-1 items-stretch gap-2">
        <button type="button" className="btn-mesic" onClick={onZpet} disabled={zpetVypnuto} aria-label="Předchozí období" title="Předchozí období">
          <ChevronLeft className="h-5 w-5 sm:h-4 sm:w-4" />
        </button>
        <div className="flex min-h-[44px] flex-1 items-center justify-center whitespace-nowrap rounded-xl border border-brand/20 bg-white px-3 text-base font-bold text-slate-900 sm:min-h-0 sm:py-2 sm:text-sm">
          {popis}
        </div>
        <button type="button" className="btn-mesic" onClick={onVpred} disabled={vpredVypnuto} aria-label="Následující období" title="Následující období">
          <ChevronRight className="h-5 w-5 sm:h-4 sm:w-4" />
        </button>
      </div>
      {naDnesek && <button type="button" className="btn btn-secondary shrink-0" onClick={naDnesek}>{popisDneska}</button>}
    </div>
  );
}

/* --- Prázdný stav a načítání --------------------------------------------- */

/** Prázdný stav něco říká a nabídne první krok. */
export function EmptyState({ icon: Ikona, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      {Ikona && (
        <span className="mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand-deep">
          <Ikona className="h-6 w-6" />
        </span>
      )}
      <p className="font-semibold text-slate-900">{title}</p>
      {hint && <p className="max-w-sm text-sm text-slate-500">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Načítání seznamu. Nahrazuje textové „Načítám…“ v obsahu stránky. */
export function SkeletonRows({ rows = 6 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-brand-light" />
      ))}
    </div>
  );
}

/** Načítání dlaždic. */
export function SkeletonCards({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="glass p-4">
          <div className="mb-3 h-9 w-9 animate-pulse rounded-xl bg-brand-light" />
          <div className="mb-2 h-3 w-20 animate-pulse rounded bg-brand-light" />
          <div className="h-6 w-16 animate-pulse rounded bg-brand-light" />
        </div>
      ))}
    </div>
  );
}

/** Chybová hláška se zopakováním, ať se kvůli výpadku sítě nemusí obnovovat stránka. */
export function ErrorNote({ children, onRetry }) {
  if (!children) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
      <span>{children}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="inline-flex min-h-[40px] items-center px-1 font-semibold underline sm:min-h-0 sm:px-0">
          Zkusit znovu
        </button>
      )}
    </div>
  );
}

/** Svislá čárka mezi částmi pilulky. Tečka „·“ se v adminu nepoužívá. */
export function Oddelovac() {
  return <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" />;
}

/* ==========================================================================
   Fáze 4a (4. 9. 2026): prvky, na kterých stojí sekce Operations.
   Zadání: docs/redesign-2-zadani.md, kapitola 4.4.
   ========================================================================== */

/**
 * Karta se seznamem. Na rozdíl od `Card` nemá tělo odsazené, takže řádky jdou
 * od kraje ke kraji a dají se oddělit linkou. Lišta je stejná jako u `Card`
 * s `lista`, takže obě karty vypadají v jedné stránce jednotně.
 *
 * `sbalitelne` udělá z lišty tlačítko; `otevreno` a `onToggle` řídí stav.
 */
export function KartaSeznam({
  ikona: Ikona,
  title,
  podtitulek,
  odznak,
  odznakTon = "bila", // bila | zluta | cervena
  akce,
  sbalitelne = false,
  otevreno = true,
  onToggle,
  className = "",
  children,
}) {
  // 12.9.2026: lišta se kreslí přes `HlavickaDlazdice`, aby byla v adminu
  // jediná tmavá hlavička karty. Dřív měla vlastní plochý gradient a
  // bezpatkový nadpis, takže karty v Operations vypadaly jinak než dlaždice
  // na Přehledu. Prop `odznakTon="zluta"` zůstává kvůli voláním z dřívějška.
  return (
    <div className={`glass min-w-0 overflow-hidden !p-0 ${className}`}>
      <div data-nadpis-karty>
        <HlavickaDlazdice
          ikona={Ikona}
          nazev={title}
          podtitulek={podtitulek}
          odznak={odznak}
          odznakTon={odznakTon === "zluta" ? "zluty" : odznakTon === "cervena" ? "cerveny" : "bily"}
          akce={akce}
          sbalitelne={sbalitelne}
          otevreno={otevreno}
          onToggle={onToggle}
        />
      </div>
      {otevreno && children}
    </div>
  );
}

/**
 * Pruh sekce uvnitř karty. Buď jen popisek (statický), nebo rozbalovací
 * skupina (piloti, role, sloupce). Nahrazuje `PilotSkupina`, `ProjektSkupina`
 * a role-dropdowny v Meetinzích.
 */
export function SekcePruh({
  title,
  pocet,
  ikona: Ikona,
  iniciály,
  ton = "znacka",
  otevreno,
  onToggle,
  pocetTon,
  className = "",
}) {
  const t = tonPodleKlice(ton);
  const rozbalovaci = typeof onToggle === "function";
  const tp = tonPodleKlice(pocetTon || (otevreno ? "znacka" : "neutralni"));
  const vnitrek = (
    <>
      {iniciály != null ? (
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${otevreno ? "bg-brand-300 text-brand-950" : "bg-slate-200 text-slate-600"}`}>
          {iniciály}
        </span>
      ) : Ikona ? (
        <Ikona className={`h-4 w-4 shrink-0 ${t.text}`} />
      ) : (
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${t.plny}`} />
      )}
      <span className={`min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-wider ${otevreno ? t.text : "text-slate-600"}`}>
        {title}
      </span>
      {pocet != null && (
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${otevreno ? "bg-white " + t.text : tp.pilulka}`}>
          {pocet}
        </span>
      )}
      {rozbalovaci && (
        otevreno
          ? <ChevronDown className={`h-4 w-4 shrink-0 ${t.text}`} />
          : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
      )}
    </>
  );
  const tridy = `flex min-h-[48px] w-full items-center gap-2 px-4 py-2 text-left ${otevreno ? t.pole : "bg-white border-t border-slate-100"} ${className}`;
  return rozbalovaci
    ? <button type="button" onClick={onToggle} aria-expanded={!!otevreno} className={tridy}>{vnitrek}</button>
    : <div className={tridy}>{vnitrek}</div>;
}

/**
 * Upravit a smazat u řádku. Krátký text: tlačítka na řádku. Dlouhý text:
 * `podTextem` je posadí pod obsah, aby text měl celou šířku (rozhodnutí 4. 9.).
 */
export function AkceRadku({ onUpravit, onSmazat, popisUpravit = "Upravit", popisSmazat = "Smazat", className = "" }) {
  return (
    <div className={`flex shrink-0 items-center gap-2 ${className}`}>
      {onUpravit && (
        <button type="button" onClick={onUpravit} aria-label={popisUpravit} title={popisUpravit} className="btn-ikona">
          <Pencil className="h-5 w-5" />
        </button>
      )}
      {onSmazat && (
        <button type="button" onClick={onSmazat} aria-label={popisSmazat} title={popisSmazat} className="btn-ikona text-rose-600">
          <Trash2 className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

/**
 * Segmentový přepínač. Kontejner má vlastní rámeček a stín, aby bylo poznat,
 * kde začíná a končí (rozhodnutí 4. 9. 2026).
 * `items`: [{ key, label, tonTextu? }]
 */
export function Segment({ items = [], value, onChange, className = "", "aria-label": ariaLabel }) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={`flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1 ${className}`}>
      {items.map((it) => {
        const aktivni = it.key === value;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={aktivni}
            onClick={() => onChange?.(it.key)}
            className={`min-h-[48px] min-w-0 flex-1 rounded-xl text-sm transition-colors sm:min-h-[40px] ${
              aktivni
                ? `border border-slate-200 bg-white font-bold shadow-glass ${it.tonTextu || "text-brand-deep"}`
                : "font-semibold text-slate-500"
            }`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/** Obal filtrů. Na telefonu rozbalený přímo na stránce, ne pod tlačítkem. */
export function PruhFiltru({ children, onZrusit, zapnuto = false, className = "" }) {
  return (
    <div className={`glass min-w-0 space-y-2 !p-3 ${className}`}>
      {children}
      {zapnuto && onZrusit && (
        <button type="button" onClick={onZrusit} className="btn btn-secondary btn-sm w-full">
          <X className="h-4 w-4" /> Zrušit vše
        </button>
      )}
    </div>
  );
}

/**
 * Filtr na celou šířku podle vzoru z PM: štítek, vybraná hodnota, šipka.
 * Po rozbalení seznam se zaškrtávacími políčky včetně volby „Vše".
 * `items`: [{ key, label, ton?, popis? }]
 * `value`: pole klíčů (vícenásobný výběr) nebo klíč (jednoduchý výběr).
 */
export function FiltrRadek({
  stitek,
  items = [],
  value,
  onChange,
  vice = true,
  popisVse = "Vše",
  className = "",
}) {
  const [otevreno, setOtevreno] = useState(false);
  const vybrane = vice ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const vseVybrano = vice && vybrane.length === 0;
  const popis = vseVybrano
    ? popisVse
    : items.filter((i) => vybrane.includes(i.key)).map((i) => i.label).join(", ") || popisVse;

  const prepni = (klic) => {
    if (!vice) { onChange?.(klic); setOtevreno(false); return; }
    onChange?.(vybrane.includes(klic) ? vybrane.filter((k) => k !== klic) : [...vybrane, klic]);
  };

  return (
    <div className={`min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        aria-expanded={otevreno}
        className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl border bg-white px-3 text-left ${otevreno ? "border-2 border-brand" : "border-slate-300"}`}
      >
        <span className="shrink-0 rounded-full bg-brand-100 px-3 py-1.5 text-sm font-bold text-brand-deep">{stitek}</span>
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-slate-900 sm:text-[17px]">{popis}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${otevreno ? "rotate-180 text-brand" : "text-slate-400"}`} />
      </button>
      {otevreno && (
        <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-glass">
          {vice && (
            <button type="button" onClick={() => onChange?.([])} className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left">
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${vseVybrano ? "border-brand bg-brand" : "border-slate-300 bg-white"}`}>
                {vseVybrano && <Check className="h-4 w-4 text-white" />}
              </span>
              <span className="min-w-0 flex-1 text-[15px] font-semibold text-slate-800">{popisVse}</span>
            </button>
          )}
          {items.map((it) => {
            const zapnuto = vybrane.includes(it.key);
            const t = tonPodleKlice(it.ton || "neutralni");
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => prepni(it.key)}
                className="flex min-h-[56px] w-full items-center gap-3 border-t border-slate-100 px-4 text-left"
              >
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${zapnuto ? "border-brand bg-brand" : "border-slate-300 bg-white"}`}>
                  {zapnuto && <Check className="h-4 w-4 text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-[15px] ${zapnuto ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{it.label}</span>
                  {it.popis && <span className="block truncate text-[11px] text-slate-500">{it.popis}</span>}
                </span>
                {it.ton && <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${t.plny}`} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Hláška ve třech tónech. Nahrazuje čtyři různé podoby chyby v Operations.
 * `ton`: "problem" | "pozor" | "ok"
 */
export function Hlaska({ ton = "problem", title, children, akce, className = "" }) {
  const sada = {
    problem: { obal: "border-rose-200 bg-rose-50", nadpis: "text-rose-800", text: "text-rose-700", Ikona: AlertTriangle, ikona: "text-rose-600" },
    pozor: { obal: "border-amber-200 bg-amber-50", nadpis: "text-amber-900", text: "text-amber-800", Ikona: Clock, ikona: "text-amber-600" },
    ok: { obal: "border-emerald-200 bg-emerald-50", nadpis: "text-emerald-800", text: "text-emerald-700", Ikona: CheckCircle2, ikona: "text-emerald-600" },
  }[ton] || {};
  const Ikona = sada.Ikona || AlertTriangle;
  return (
    <div className={`flex flex-wrap items-start gap-3 rounded-2xl border px-4 py-3 ${sada.obal} ${className}`}>
      <Ikona className={`mt-0.5 h-5 w-5 shrink-0 ${sada.ikona}`} />
      {/* 19. 10. 2026: na úzkém displeji tlačítka (akce) spadnou pod text, ať se text nemačká do sloupce. */}
      <div className="min-w-0 flex-1 basis-48">
        {title && <div className={`text-sm font-bold ${sada.nadpis}`}>{title}</div>}
        {children && <div className={`text-sm ${sada.text} ${title ? "mt-0.5" : ""}`}>{children}</div>}
      </div>
      {akce}
    </div>
  );
}

/** Ikonové tlačítko „zkusit znovu" do hlášky. */
export function AkceZnovu({ onClick, className = "" }) {
  return (
    <button type="button" onClick={onClick} aria-label="Zkusit znovu" title="Zkusit znovu"
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-rose-300 bg-white text-rose-700 ${className}`}>
      <RefreshCw className="h-5 w-5" />
    </button>
  );
}

/**
 * Výběr seznamu. Sheet zespodu se skupinami a řádky; nahrazuje vodorovné
 * rolování kanbanu na telefonu a spojuje sloupce boardu s pevnými skupinami.
 * `skupiny`: [{ nadpis, polozky: [{ key, label, popis, pocet, ton, ikona }] }]
 */
export function VyberSeznamu({ open, onClose, title = "Který seznam", ikona: Ikona, skupiny = [], value, onChange }) {
  useZamekRolovani(!!open);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    /* klavesnice-ok: jen seznam voleb, žádné pole (14. 9. 2026, fáze 6) */
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brand-dark/50 backdrop-blur-sm sm:items-center" onClick={onClose}>
      <div className="safe-bottom max-h-[calc(var(--vv-vyska,100vh)*0.92)] w-full max-w-md animate-sheet-up overflow-y-auto overscroll-contain rounded-t-2xl bg-white shadow-2xl sm:mx-4 sm:animate-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3">
          {Ikona && <Ikona className="h-5 w-5 shrink-0 text-white/80" />}
          <div className="min-w-0 flex-1 font-serif text-lg font-bold leading-tight text-white">{title}</div>
          <button type="button" onClick={onClose} aria-label="Zavřít"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white sm:h-9 sm:w-9">
            <X className="h-5 w-5" />
          </button>
        </div>
        {skupiny.map((sk) => (
          <div key={sk.nadpis || "bez"}>
            {sk.nadpis && (
              <div className="bg-brand-100 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-deep">{sk.nadpis}</div>
            )}
            <div className="divide-y divide-slate-100">
              {sk.polozky.map((p) => {
                const aktivni = p.key === value;
                const t = tonPodleKlice(p.ton || "neutralni");
                const PIkona = p.ikona;
                return (
                  <button key={p.key} type="button" onClick={() => { onChange?.(p.key); onClose?.(); }}
                    className={`relative flex min-h-[60px] w-full items-center gap-3 px-4 py-2 text-left ${aktivni ? "bg-brand-50" : ""}`}>
                    {aktivni && <span className="absolute inset-y-0 left-0 w-1.5 bg-brand" />}
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${aktivni ? "bg-brand-200" : t.pole}`}>
                      {PIkona ? <PIkona className={`h-4 w-4 ${aktivni ? "text-brand-deep" : t.text}`} /> : <span className={`h-2.5 w-2.5 rounded-full ${t.plny}`} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-bold ${aktivni ? "text-brand-deep" : "text-slate-900"}`}>{p.label}</span>
                      {p.popis && <span className="block truncate text-[11px] text-slate-500">{p.popis}</span>}
                    </span>
                    {p.pocet != null && (
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-sm font-bold tabular-nums ${aktivni ? "bg-brand text-white" : t.pilulka}`}>{p.pocet}</span>
                    )}
                    {aktivni && <Check className="h-4 w-4 shrink-0 text-brand" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

/** Dlaždice čísla. Barevný proužek nahoře nese význam. */
export function Dlazdice({ popisek, hodnota, ton = "znacka", doplnky, hodnotaTon, className = "" }) {
  const t = tonPodleKlice(ton);
  const ht = hodnotaTon ? tonPodleKlice(hodnotaTon).text : "text-slate-900";
  return (
    <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glass ${className}`}>
      <div className={`h-1.5 ${t.plny}`} />
      <div className="p-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{popisek}</div>
        <div className={`mt-0.5 text-xl font-bold tabular-nums sm:text-2xl ${ht}`}>{hodnota}</div>
        {doplnky && <div className="mt-1 flex flex-wrap gap-1">{doplnky}</div>}
      </div>
    </div>
  );
}

/**
 * Dlaždice, která zároveň filtruje (9. 9. 2026, fáze 4b; nový vzhled 2. 10. 2026,
 * mockup „Karta klientky a dlaždice“, varianta A). Popisek vlevo s barevnou tečkou
 * (nebo ikonou) podle tónu, číslo vpravo. Zapnutá je celá tmavá se zatržítkem.
 * Nápověda sedí mimo tlačítko schválně: `InfoTip` je sám tlačítko a tlačítko
 * v tlačítku není platné HTML.
 */
export function DlazdiceFiltr({ popisek, hodnota, ton = "znacka", ikona: Ikona, info, zapnuto = false, onClick, className = "" }) {
  const t = tonPodleKlice(ton);
  const klikaci = typeof onClick === "function";
  const zap = klikaci && zapnuto;
  return (
    <div
      className={`flex min-h-[64px] items-stretch overflow-hidden rounded-2xl border shadow-glass transition ${
        zap ? "border-brand-900 bg-brand-900 text-white" : "border-slate-200 bg-white text-slate-900"
      } ${className}`}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!klikaci}
        aria-pressed={klikaci ? zapnuto : undefined}
        className={`flex min-w-0 flex-1 flex-col justify-between gap-1 px-3 py-2.5 text-left sm:px-3.5 ${klikaci ? (zap ? "" : "active:bg-brand-light/60 sm:hover:bg-brand-50/60") : "cursor-default"}`}
      >
        {/* 15. 10. 2026 (Láďa, schválený náhled): popisek nahoře, hodnota pod ním, u všech
            dlaždic stejně. Dřív byla hodnota vpravo a u dlouhé částky (128 200 Kč) se
            zalomila pod popisek jen v jedné dlaždici, řada pak vypadala nesourodě. */}
        <span className="flex w-full min-w-0 items-start gap-2 sm:gap-2.5">
          {Ikona
            ? <Ikona className={`mt-px h-4 w-4 shrink-0 ${zap ? "text-white/80" : t.text}`} />
            : <span aria-hidden className={`mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full ${zap ? "bg-white/70" : t.plny}`} />}
          <span className={`min-w-0 flex-1 text-[13px] font-semibold leading-tight sm:text-[14px] ${zap ? "text-white/80" : "text-slate-600"}`}>{popisek}</span>
          {zap && <Check className="h-[18px] w-[18px] shrink-0" strokeWidth={2.6} />}
        </span>
        <span className="whitespace-nowrap text-xl font-bold tabular-nums sm:text-2xl">{hodnota}</span>
      </button>
      {info && <span className={`flex shrink-0 items-center pr-1.5 sm:pr-2.5 ${zap ? "text-white/80" : ""}`}>{info}</span>}
    </div>
  );
}

/* --- Zapínátko ------------------------------------------------------------ */

/**
 * Řádek se zapínátkem (4. 9. 2026). Filtry typu „jen projekty" vypadaly jako
 * rozbalovací řádek, ale nic nerozbalovaly. Zapínátko je vidět na první pohled.
 */
export function Zapinatko({ zapnuto, onZmena, popisek, ikona: Ikona, className = "" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!zapnuto}
      onClick={() => onZmena?.(!zapnuto)}
      className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl border bg-white px-3 text-left ${zapnuto ? "border-2 border-brand bg-brand-50" : "border-slate-300"} ${className}`}
    >
      {Ikona && <Ikona className={`h-5 w-5 shrink-0 ${zapnuto ? "text-brand-deep" : "text-slate-400"}`} />}
      <span className={`min-w-0 flex-1 text-[15px] font-bold ${zapnuto ? "text-brand-deep" : "text-slate-900"}`}>{popisek}</span>
      <span className={`flex h-7 w-12 shrink-0 items-center rounded-full p-0.5 transition-colors ${zapnuto ? "justify-end bg-brand" : "justify-start bg-slate-200"}`}>
        <span className="h-6 w-6 rounded-full bg-white shadow-glass" />
      </span>
    </button>
  );
}


/* --- Nabídka pod třemi tečkami ------------------------------------------- */

/**
 * Vedlejší a nebezpečné akce stránky (14. 9. 2026, fáze 6). Do hlavičky patří
 * jen dvě tři časté akce jako ikony, zbytek sem. Na telefonu se otevře jako
 * spodní sheet (Modal), na počítači jako panel pod tlačítkem.
 *
 * `polozky`: [{ klic, ikona, popis, onClick, nebezpecna?, disabled? }]
 * `tmava`: tlačítko na tmavé liště (hlavička stránky), jinak bílé .btn-ikona
 */
export function NabidkaAkci({ polozky = [], tmava = false, popis = "Další volby", className = "" }) {
  const [open, setOpen] = useState(false);
  const [pozice, setPozice] = useState(null);
  const tlacitko = useRef(null);
  const panel = useRef(null);
  const uzka = useUzkaObrazovka();

  useEffect(() => {
    if (!open || uzka) return undefined;
    const r = tlacitko.current?.getBoundingClientRect();
    if (r) setPozice({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    const mimo = (e) => {
      if (panel.current?.contains(e.target) || tlacitko.current?.contains(e.target)) return;
      setOpen(false);
    };
    const klavesa = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", mimo);
    document.addEventListener("keydown", klavesa);
    window.addEventListener("resize", () => setOpen(false), { once: true });
    return () => {
      document.removeEventListener("mousedown", mimo);
      document.removeEventListener("keydown", klavesa);
    };
  }, [open, uzka]);

  const vyber = (p) => {
    setOpen(false);
    p.onClick?.();
  };

  const seznam = (
    <ul className="m-0 list-none p-0">
      {polozky.map((p) => {
        const Ikona = p.ikona;
        return (
          <li key={p.klic || p.popis}>
            {p.oddelit && <div aria-hidden className="mx-1.5 my-1 h-px bg-brand-light" />}
            <button
              type="button"
              disabled={p.disabled}
              onClick={() => vyber(p)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px] font-semibold transition disabled:opacity-40 sm:py-2.5 sm:text-sm ${
                p.nebezpecna ? "text-rose-700 hover:bg-rose-50" : "text-slate-800 hover:bg-brand-light"
              }`}
            >
              {Ikona && <Ikona className="h-5 w-5 shrink-0" />}
              <span className="min-w-0 flex-1">{p.popis}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <button
        ref={tlacitko}
        type="button"
        aria-label={popis}
        title={popis}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={tmava
          ? `flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 sm:h-10 sm:w-10 sm:hover:bg-white/20 ${className}`
          : `btn-ikona ${className}`}
        data-akce-hlavicky
      >
        <MoreHorizontal className="h-[18px] w-[18px]" />
      </button>
      {open && uzka && (
        <Modal open onClose={() => setOpen(false)} title={popis} size="sm">
          {seznam}
        </Modal>
      )}
      {open && !uzka && pozice && createPortal(
        <div
          ref={panel}
          role="menu"
          style={{ position: "fixed", top: pozice.top, right: pozice.right }}
          className="z-[70] min-w-[220px] rounded-2xl border border-brand/10 bg-white p-1.5 shadow-2xl animate-zoom-in"
        >
          {seznam}
        </div>,
        document.body,
      )}
    </>
  );
}

/* --- Filtr na počítači ---------------------------------------------------- */

/**
 * Plovoucí rozbalovací nabídka (FW-148, 14. 10. 2026). Kreslí se přes portál do
 * body s `fixed` a z-[91], takže ji nepřekryje žádný sousední blok (karta s
 * backdrop-blur, sticky lišta) a neuřízne ji rodič s overflow. Pozice se bere
 * z kotvy (obal tlačítka), přepočítá se při rolování a změně okna, a když dole
 * není místo, otevře se nahoru. Zavírá se klepnutím mimo kotvu i nabídku (ne
 * posunem stránky) a Esc.
 * Stejný přístup jako MultiSelectFilter.
 */
export function PlovouciNabidka({ kotva, otevreno, onZavrit, zarovnani = "vlevo", sirka = "kotva", minSirka = 220, maxVyska = 320, className = "", role, children }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);
  const zavritRef = useRef(onZavrit);
  zavritRef.current = onZavrit;
  const spocitej = useCallback(() => {
    const el = kotva?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(vw - 16, sirka === "kotva" ? Math.max(minSirka, r.width) : sirka);
    let left = zarovnani === "vpravo" ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, vw - w - 8));
    const dole = vh - r.bottom - 14;
    const nahore = r.top - 14;
    const nahoru = dole < Math.min(maxVyska, 200) && nahore > dole;
    const vyska = Math.max(120, Math.min(maxVyska, nahoru ? nahore : dole));
    setPos(nahoru ? { left, width: w, maxHeight: vyska, bottom: vh - r.top + 6 } : { left, width: w, maxHeight: vyska, top: r.bottom + 6 });
  }, [kotva, sirka, minSirka, maxVyska, zarovnani]);
  useLayoutEffect(() => { if (otevreno) spocitej(); else setPos(null); }, [otevreno, spocitej]);
  useEffect(() => {
    if (!otevreno) return undefined;
    const naRolovani = (e) => { if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return; spocitej(); };
    // Zavírá se jen klepnutím mimo (dotek bez posunu). Posun stránky prstem
    // nabídku nezavře (FW-152), proto se rozhoduje až při puštění.
    let start = null;
    const mimo = (t) => !(panelRef.current?.contains(t) || kotva?.current?.contains(t));
    const naDotek = (e) => { start = mimo(e.target) ? { x: e.clientX, y: e.clientY } : null; };
    const naPusteni = (e) => {
      if (!start) return;
      const posun = Math.abs(e.clientX - start.x) + Math.abs(e.clientY - start.y);
      start = null;
      if (posun < 10) zavritRef.current?.();
    };
    const naZruseni = () => { start = null; };
    const naKlavesu = (e) => { if (e.key === "Escape") zavritRef.current?.(); };
    window.addEventListener("scroll", naRolovani, true);
    window.addEventListener("resize", spocitej);
    window.visualViewport?.addEventListener("resize", spocitej);
    document.addEventListener("pointerdown", naDotek, true);
    document.addEventListener("pointerup", naPusteni, true);
    document.addEventListener("pointercancel", naZruseni, true);
    document.addEventListener("keydown", naKlavesu);
    return () => {
      window.removeEventListener("scroll", naRolovani, true);
      window.removeEventListener("resize", spocitej);
      window.visualViewport?.removeEventListener("resize", spocitej);
      document.removeEventListener("pointerdown", naDotek, true);
      document.removeEventListener("pointerup", naPusteni, true);
      document.removeEventListener("pointercancel", naZruseni, true);
      document.removeEventListener("keydown", naKlavesu);
    };
  }, [otevreno, spocitej, kotva]);
  if (!otevreno || !pos || typeof document === "undefined") return null;
  return createPortal(
    <div ref={panelRef} role={role} data-plovouci-nabidka="" className={`fixed z-[91] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white shadow-xl ${className}`}
      style={{ left: pos.left, width: pos.width, maxHeight: pos.maxHeight, top: pos.top, bottom: pos.bottom }}>
      {children}
    </div>,
    document.body
  );
}

/**
 * Kompaktní filtr do vodorovné lišty (4. 9. 2026). Na telefonu jsou filtry
 * celoplošné řádky (`FiltrRadek`), na širokém monitoru by se roztáhly přes
 * celou obrazovku, proto tahle úzká varianta s rozbalovacím panelem.
 *
 * `items`: [{ key, label, popis?, ton? }]
 * `value`: pole klíčů (vícenásobný výběr) nebo klíč (jednoduchý výběr)
 */
export function FiltrTlacitko({
  popis,
  items = [],
  value,
  onChange,
  vice = false,
  popisVse = "Vše",
  ikona: Ikona,
  className = "",
}) {
  const [otevreno, setOtevreno] = useState(false);
  const ref = useRef(null);

  const vybrane = vice ? (Array.isArray(value) ? value : []) : (value && value !== "vse" ? [value] : []);
  const zapnuto = vybrane.length > 0;
  const text = popis || (zapnuto
    ? items.filter((i) => vybrane.includes(i.key)).map((i) => i.label).join(", ") || popisVse
    : popisVse);

  const prepni = (klic) => {
    if (!vice) { onChange?.(klic); setOtevreno(false); return; }
    onChange?.(vybrane.includes(klic) ? vybrane.filter((k) => k !== klic) : [...vybrane, klic]);
  };

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        aria-expanded={otevreno}
        className={`flex h-11 w-full items-center gap-2 rounded-xl px-3.5 text-left text-[15px] ${
          zapnuto ? "border-2 border-brand bg-brand-50 font-bold text-brand-deep" : "border border-slate-300 bg-white font-semibold text-slate-700"
        }`}
      >
        {Ikona && <Ikona className={`h-5 w-5 shrink-0 ${zapnuto ? "text-brand-deep" : "text-slate-400"}`} />}
        <span className="min-w-0 flex-1 truncate">{text}</span>
        {vice && vybrane.length > 1 && (
          <span className="shrink-0 rounded-full bg-brand px-2 text-[11px] font-bold text-white">{vybrane.length}</span>
        )}
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${otevreno ? "rotate-180" : ""} ${zapnuto ? "" : "text-slate-400"}`} />
      </button>
      <PlovouciNabidka kotva={ref} otevreno={otevreno} onZavrit={() => setOtevreno(false)} className="py-1 shadow-glass-lg">
          {vice && (
            <button type="button" onClick={() => { onChange?.([]); }} className="flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left hover:bg-slate-50">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${vybrane.length === 0 ? "border-brand bg-brand" : "border-slate-300 bg-white"}`}>
                {vybrane.length === 0 && <Check className="h-3.5 w-3.5 text-white" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{popisVse}</span>
            </button>
          )}
          {items.map((it) => {
            const aktivni = vybrane.includes(it.key);
            return (
              <button key={it.key} type="button" onClick={() => prepni(it.key)}
                className="flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left hover:bg-slate-50">
                {vice ? (
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${aktivni ? "border-brand bg-brand" : "border-slate-300 bg-white"}`}>
                    {aktivni && <Check className="h-3.5 w-3.5 text-white" />}
                  </span>
                ) : (
                  <span className="w-5 shrink-0">{aktivni && <Check className="h-4 w-4 text-brand" />}</span>
                )}
                <span className={`min-w-0 flex-1 truncate text-sm ${aktivni ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>{it.label}</span>
              </button>
            );
          })}
      </PlovouciNabidka>
    </div>
  );
}

/** Štítek se sjednoceným tónem. Nejmenší písmo je 11 px. */
export function Stitek({ ton = "neutralni", velikost = "bezna", ikona: Ikona, className = "", children }) {
  const t = tonPodleKlice(ton);
  const velikosti = {
    mala: "px-2 py-0.5 text-[11px]",
    bezna: "px-2.5 py-1 text-xs",
    velka: "px-3 py-1.5 text-sm",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-bold ${velikosti[velikost] || velikosti.bezna} ${t.pilulka} ${className}`}>
      {Ikona && <Ikona className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}
